import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import {
  ATTENDANCE_STATUSES,
  ATTENDANCE_STATUS_LABELS,
  checkIn,
  checkOut,
  exportAttendance,
  listAttendance,
  type AttendanceFilters,
  type AttendanceRead,
  type AttendanceStatus,
} from '@/features/attendance/api'
import { AttendanceFormDialog } from '@/features/attendance/AttendanceFormDialog'
import { useAuth } from '@/features/auth/store'
import { listEmployees } from '@/features/employees/api'
import { listJobPostingLocations } from '@/features/recruitment/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<AttendanceStatus, string> = {
  present: 'bg-success/10 text-success',
  late: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  half_day: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  absent: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  on_leave: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * One present/absent/late/half-day/on-leave record per employee per day (lite slice of
 * PRD P2-05 -- see `attendance_models.py`). HR marks and corrects any employee's day here;
 * everyone with a `leave` Create grant (field reps, store staff, other employees) uses the
 * check-in/out buttons to record their own.
 */
export function Attendance() {
  const can = useAuth((s) => s.can)
  const canManage = can('leave', 'update')
  const canSelf = can('leave', 'create')
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<AttendanceFilters>({ page: 1 })
  const [dialog, setDialog] = useState<{ attendance?: AttendanceRead } | null>(null)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['attendance', filters],
    queryFn: () => listAttendance(filters),
    retry: false,
  })

  const employees = useQuery({
    queryKey: ['employees', 'active-for-attendance-filter'],
    queryFn: () => listEmployees({ status: 'active' }),
    retry: false,
    enabled: canManage,
  })

  const locations = useQuery({
    queryKey: ['job-posting-locations'],
    queryFn: () => listJobPostingLocations(),
    retry: false,
    enabled: canManage,
  })

  const punch = useMutation({
    mutationFn: (kind: 'in' | 'out') => (kind === 'in' ? checkIn() : checkOut()),
    onSuccess: (_, kind) => {
      void queryClient.invalidateQueries({ queryKey: ['attendance'] })
      toast.success(kind === 'in' ? 'Checked in.' : 'Checked out.')
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Could not reach the server.')
    },
  })

  const rows = data?.data ?? []
  const meta = data?.meta

  const set = (patch: Partial<AttendanceFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  const columns: Column<AttendanceRead>[] = [
    {
      key: 'employee_name',
      header: 'Employee',
      className: 'font-medium',
      cell: (row) =>
        canManage ? (
          <button
            type="button"
            onClick={() => setDialog({ attendance: row })}
            className="text-left hover:underline"
          >
            {row.employee_name ?? row.employee_id}
          </button>
        ) : (
          row.employee_name ?? row.employee_id
        ),
    },
    {
      key: 'date',
      header: 'Date',
      cell: (row) => row.date,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}>
          {ATTENDANCE_STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      key: 'check_in_at',
      header: 'Check in',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => formatTime(row.check_in_at),
    },
    {
      key: 'check_out_at',
      header: 'Check out',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => formatTime(row.check_out_at),
    },
    {
      key: 'location_name',
      header: 'Location',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => row.location_name ?? '—',
    },
    {
      key: 'marked_by_name',
      header: 'Marked by',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => row.marked_by_name ?? '—',
    },
  ]

  const employeeOptions = employees.data?.data ?? []
  const locationOptions = locations.data ?? []

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Attendance</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} record${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canSelf && (
            <>
              <Button variant="secondary" loading={punch.isPending} onClick={() => punch.mutate('in')}>
                Check in
              </Button>
              <Button variant="secondary" loading={punch.isPending} onClick={() => punch.mutate('out')}>
                Check out
              </Button>
            </>
          )}
          {canManage && <Button onClick={() => setDialog({})}>Mark attendance</Button>}
        </div>
      </header>

      <Card>
        <CardHeader title="Attendance records" />
        <DataTable
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          caption="Attendance"
          isLoading={isLoading}
          error={error}
          emptyMessage="No attendance records match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              {canManage && (
                <>
                  <label className="sr-only" htmlFor="attendance-employee">
                    Employee
                  </label>
                  <select
                    id="attendance-employee"
                    className={SELECT_CLASS}
                    value={filters.employee_id ?? ''}
                    onChange={(event) => set({ employee_id: event.target.value || undefined })}
                  >
                    <option value="">Any employee</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.full_name ?? employee.employee_code}
                      </option>
                    ))}
                  </select>

                  <label className="sr-only" htmlFor="attendance-location">
                    Location
                  </label>
                  <select
                    id="attendance-location"
                    className={SELECT_CLASS}
                    value={filters.location_id ?? ''}
                    onChange={(event) => set({ location_id: event.target.value || undefined })}
                  >
                    <option value="">Any location</option>
                    {locationOptions.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label className="sr-only" htmlFor="attendance-from">
                From
              </label>
              <input
                id="attendance-from"
                type="date"
                className={SELECT_CLASS}
                value={filters.date_from ?? ''}
                onChange={(event) => set({ date_from: event.target.value || undefined })}
              />

              <label className="sr-only" htmlFor="attendance-to">
                To
              </label>
              <input
                id="attendance-to"
                type="date"
                className={SELECT_CLASS}
                value={filters.date_to ?? ''}
                onChange={(event) => set({ date_to: event.target.value || undefined })}
              />

              <label className="sr-only" htmlFor="attendance-status">
                Status
              </label>
              <select
                id="attendance-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) => set({ status: event.target.value as AttendanceStatus | '' })}
              >
                <option value="">Any status</option>
                {ATTENDANCE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {ATTENDANCE_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>

              {(filters.employee_id ||
                filters.location_id ||
                filters.date_from ||
                filters.date_to ||
                filters.status) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ page: 1 })}>
                  Clear
                </Button>
              )}

              <ExportButton
                filename="attendance"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportAttendance(filters, format)}
                errorMessage="Could not export attendance."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      {dialog && (
        <AttendanceFormDialog open attendance={dialog.attendance} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}
